export const CODE_REGEXP = (length: number) =>
	new RegExp(`^[a-z0-9]{${length}}$`);

/**
 * Generates a random alphanumeric code with guaranteed minimum counts of letters and numbers.
 * Ensures at least 4 letters and at least 4 numbers are present in the generated code.
 *
 * @param length The total length of the code to generate. Must be at least 8 to satisfy minimum requirements.
 * @returns A random alphanumeric string in lowercase
 * @throws Error if length is less than 8 (minimum required for 4 letters + 4 numbers)
 */
export function generateCode(options: {
	length: number;
	minLetters?: number;
	minNumbers?: number;
}): string {
	const { length, minLetters, minNumbers } = {
		minLetters: 2,
		minNumbers: 2,
		...options,
	};

	const letters = 'abcdefghijklmnopqrstuvwxyz';
	const numbers = '0123456789';

	// Generate the minimum required letters and numbers
	const letterChars: string[] = [];
	const numberChars: string[] = [];

	// Generate at least minLetters letters
	for (let i = 0; i < minLetters; i++) {
		letterChars.push(
			letters.charAt(Math.floor(Math.random() * letters.length)),
		);
	}

	// Generate at least minNumbers numbers
	for (let i = 0; i < minNumbers; i++) {
		numberChars.push(
			numbers.charAt(Math.floor(Math.random() * numbers.length)),
		);
	}

	// Fill the remaining slots with random characters from either set
	const remaining = length - minLetters - minNumbers;
	const allChars = letters + numbers;

	for (let i = 0; i < remaining; i++) {
		const randomChar = allChars.charAt(
			Math.floor(Math.random() * allChars.length),
		);
		if (/[0-9]/.test(randomChar)) {
			numberChars.push(randomChar);
		} else {
			letterChars.push(randomChar);
		}
	}

	// Combine and shuffle the characters
	const allGeneratedChars = [...letterChars, ...numberChars];

	// Fisher-Yates shuffle algorithm
	for (let i = allGeneratedChars.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[allGeneratedChars[i], allGeneratedChars[j]] = [
			allGeneratedChars[j],
			allGeneratedChars[i],
		];
	}

	return allGeneratedChars.join('');
}
